import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import "../css/speechFeature.css";
import { HiMiniSpeakerWave } from "react-icons/hi2";
import { FaRegCircleStop } from "react-icons/fa6";
import { FlagIcon, SpeakerWaveIcon } from "@heroicons/react/24/outline";

const SpeechFeature = forwardRef((props, ref) => {
  const [rate, setRate] = useState(1);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);

  const msgRef = useRef(new SpeechSynthesisUtterance());
  const [speaking, setSpeaking] = React.useState(false);
  const speechRef = React.useRef(null);
  // Make speak(text) & stop() available to parent via ref
  useImperativeHandle(ref, () => ({
    speak,
    stop: cancel,
  }));

  useEffect(() => {
    const fetchVoices = () => {
      const all = window.speechSynthesis.getVoices();
      // A machine can offer 50 voices across every language; a student choosing how a
      // lesson is read aloud only needs the English ones. Fall back to everything if
      // the browser reports none, rather than leaving the list empty.
      const english = all.filter((voice) => /^en(-|_|$)/i.test(voice.lang || ""));
      const usable = english.length > 0 ? english : all;
      setVoices(usable);
      setSelectedVoice((current) => current || usable[0] || null);
    };

    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = fetchVoices;
    }

    fetchVoices();
    msgRef.current.rate = rate;

    // Set up speech events
    msgRef.current.onstart = () => setSpeaking(true);
    msgRef.current.onend = () => setSpeaking(false);
    msgRef.current.onerror = () => setSpeaking(false);

    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  useEffect(() => {
    msgRef.current.rate = rate;
  }, [rate]);

  useEffect(() => {
    msgRef.current.voice = selectedVoice;
  }, [selectedVoice]);

  // 👇 Accept text from parent
  const speak = (textToSpeak) => {
    const cleanedText = (textToSpeak || "").replace(/_+/g, "space");

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    msgRef.current.text = cleanedText;
    msgRef.current.voice = selectedVoice;
    window.speechSynthesis.speak(msgRef.current);
    // setSpeaking(true); // now handled by onstart
  };

  const cancel = () => {
    window.speechSynthesis.cancel();
    // setSpeaking(false); // now handled by onend/onerror
  };

  const handleRateChange = (newRate) => {
    setRate(Math.max(0.1, Math.min(newRate, 10)));
  };

  const handleVoiceChange = (voiceName) => {
    const voice = voices.find((v) => v.name === voiceName);
    setSelectedVoice(voice);
  };
// const toggleSpeak = () => {
//   if (speechRef.current) {
//     setSpeaking(!speaking);
//     if (!speaking) {
//       speechRef.current.speak(questionData.question);
//     } else {
//       speechRef.current.cancel();
//     }
//   }
// };
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
      <button
        onClick={speaking ? cancel : () => speak(props.text)}
        className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
          speaking
            ? "bg-slate-900 text-white hover:bg-slate-800"
            : "bg-orange-500 text-white hover:bg-orange-600"
        }`}
        aria-label={speaking ? "Stop reading" : "Read this lesson aloud"}
        title={speaking ? "Stop reading" : "Read this lesson aloud"}
      >
        {speaking ? (
          <FaRegCircleStop className="h-4 w-4" />
        ) : (
          <SpeakerWaveIcon className="h-4 w-4" />
        )}
        {speaking ? "Stop" : "Listen to this lesson"}
      </button>

      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          Speed
        </span>
        <button
          onClick={() => handleRateChange(rate - 0.25)}
          aria-label="Slower"
          className="h-7 w-7 rounded-md border border-slate-200 text-slate-600 transition-colors hover:border-orange-300 hover:text-orange-600"
        >
          &minus;
        </button>
        <span className="w-10 text-center text-sm tabular-nums text-slate-700">
          {rate.toFixed(2)}
        </span>
        <button
          onClick={() => handleRateChange(rate + 0.25)}
          aria-label="Faster"
          className="h-7 w-7 rounded-md border border-slate-200 text-slate-600 transition-colors hover:border-orange-300 hover:text-orange-600"
        >
          +
        </button>
      </div>

      <label
        htmlFor="voices"
        className="ml-auto flex min-w-0 items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400"
      >
        Voice
        <select
          id="voices"
          onChange={(e) => handleVoiceChange(e.target.value)}
          value={selectedVoice?.name || ""}
          className="max-w-[17rem] rounded-md border border-slate-200 bg-white px-2 py-1 text-sm font-normal normal-case tracking-normal text-slate-700 focus:border-orange-400 focus:outline-none"
        >
          {voices.map((voice) => (
            <option key={voice.name} value={voice.name}>
              {voice.name} ({voice.lang})
            </option>
          ))}
        </select>
      </label>
    </div>
  );
});

export default SpeechFeature;
